import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ReportType, type ReportEntity } from '@app/domain/entities';
import {
  loadExcelJs,
  sanitizeSheetName,
  sanitizeFilename,
  addFormattedRow,
  setColumnWidths,
} from './report-export.utils';
import { REPORT_EXPORT_FILE_SERVICE, type ReportExportFileService } from './report-export-file.service';
import type { IReportExportUseCase } from './report-export.use-case';

interface StatementItem {
  date: Date | string;
  account: string;
  amount: number;
  currencyCode: string;
  description?: string;
  mcc?: number;
  category: string;
  subcategory?: string;
}

interface StatementsReportData {
  filters: {
    dateFrom?: Date;
    dateTo?: Date;
  };
  data: {
    incomes: StatementItem[];
    expenses: StatementItem[];
  };
}

@Injectable({ providedIn: 'root' })
export class ExportStatementsUseCase implements IReportExportUseCase {
  readonly reportType = ReportType.STATEMENTS;

  private readonly translate = inject(TranslateService);
  private readonly reportExportFileService = inject<ReportExportFileService>(REPORT_EXPORT_FILE_SERVICE);

  async export(report: ReportEntity): Promise<void> {
    if (!report.data) return;

    const data = report.data as StatementsReportData;
    const ExcelJS = await loadExcelJs();
    const workbook = new ExcelJS.Workbook();

    const dateHeader = 'Date';
    const accountHeader = 'Account';
    const amountHeader = 'Amount';
    const currencyHeader = 'CurrencyCode';
    const descriptionHeader = 'Description';
    const mccHeader = 'MCC';
    const categoryHeader = 'Category';
    const subcategoryHeader = 'SubCategory';

    const sections = [
      {
        title: this.translate.instant('screens.reports.labels.incomes') as string,
        items: data.data.incomes,
        includeMcc: false,
      },
      {
        title: this.translate.instant('screens.reports.labels.expenses') as string,
        items: data.data.expenses,
        includeMcc: true,
      },
    ];

    for (const section of sections) {
      const sheet = workbook.addWorksheet(sanitizeSheetName(section.title));

      const headers = [dateHeader, accountHeader, amountHeader, currencyHeader, descriptionHeader];
      if (section.includeMcc) headers.push(mccHeader);
      headers.push(categoryHeader, subcategoryHeader);

      const columnTypes: ('date' | 'currency' | 'text')[] = ['date', 'text', 'currency', 'text', 'text'];
      if (section.includeMcc) columnTypes.push('text');
      columnTypes.push('text', 'text');

      sheet.addRow(headers);
      setColumnWidths(sheet, columnTypes);

      const columnFormats: (string | undefined)[] = ['dd/mm/yyyy', undefined, undefined, undefined, undefined];
      if (section.includeMcc) columnFormats.push(undefined);
      columnFormats.push(undefined, undefined);

      for (const item of section.items) {
        const date = item.date instanceof Date ? item.date : new Date(item.date);
        const row: (Date | string | number | undefined)[] = [
          date,
          item.account,
          item.amount,
          item.currencyCode,
          item.description ?? '',
        ];
        if (section.includeMcc) row.push(item.mcc ?? '');
        row.push(item.category, item.subcategory ?? '');

        addFormattedRow(sheet, row, columnFormats);
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const typeName = this.translate.instant(`screens.reports.types.${report.type.toLowerCase()}`) as string;
    await this.reportExportFileService.saveExcelFile(
      buffer,
      `${sanitizeFilename(typeName)}-${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  }
}
